/**
 * Dependency-free single-value donut (a percentage ring). Hand-drawn SVG with
 * two stroked circles — no external chart library. Used on metric cards to show
 * a fail-rate / error-rate at a glance.
 */

interface MiniDonutProps {
  /** 0–100. */
  pct: number;
  size?: number;
  stroke?: number;
  color?: string;
  trackColor?: string;
  label?: string;
}

export function MiniDonut({
  pct,
  size = 44,
  stroke = 5,
  color = '#f87171',
  trackColor = '#1e293b',
  label,
}: MiniDonutProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label ?? `${clamped}%`}
      className="shrink-0"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${c - dash}`}
        strokeDashoffset={c / 4}
        strokeLinecap="round"
        transform={`scale(1,-1) translate(0, -${size})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="fill-slate-200"
        style={{ fontSize: size * 0.26, fontWeight: 600 }}
      >
        {Math.round(clamped)}%
      </text>
    </svg>
  );
}
